import {
    Box, Button, Flex, Input, Text,
    InputGroup, InputLeftElement
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import EventsService from '../../../../services/events/eventsService';
import CategoriesTable from '../../components/CategoriesTable/CategoriesTable';
import EditCategoryModal from '../../components/EditCategoryModal/EditCategoryModal';
import { useTranslation } from "react-i18next";
import { useAppToast } from "../../../../hooks/useAppToast";

const CategoriesPage = () => {
    const { t } = useTranslation("default");
    const [categories, setCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [newCategoryName, setNewCategoryName] = useState('');
    const [sort, setSort] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [editingCategory, setEditingCategory] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const toast = useAppToast();

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await EventsService.getCategories();
            if (response && response.data) {
                setCategories(response.data);
                setFilteredCategories(response.data);
            }
        } catch (error) {
            toast({
                title: t('category.error.fetch'),
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        // Filtrowanie kategorii
        let result = [...categories];

        if (search) {
            result = result.filter(cat =>
                cat.name.toLowerCase().includes(search.toLowerCase())
            );
        }

        // Sortowanie
        result.sort((a, b) => {
            let comparison = 0;
            if (sort === 'name') {
                comparison = a.name.localeCompare(b.name);
            } else if (sort === 'id') {
                comparison = a.id.localeCompare(b.id);
            } else if (sort === 'eventsCount') {
                comparison = (a.eventsCount || 0) - (b.eventsCount || 0);
            }
            return sortDir === 'asc' ? comparison : -comparison;
        });

        setFilteredCategories(result);
    }, [categories, search, sort, sortDir]);

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) {
            toast({
                title: t('category.error.empty_name'),
                status: 'warning',
                duration: 3000,
                isClosable: true
            });
            return;
        }

        try {
            await EventsService.createCategory(newCategoryName.trim());
            toast({
                title: t('category.success.created'),
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            setNewCategoryName('');
            await fetchCategories();
        } catch (error) {
            toast({
                title: t('category.error.create'),
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
    };

    const handleSearch = () => {
        // Wyszukiwanie jest realizowane automatycznie przez useEffect
    };

    const handleSort = (column) => {
        if (sort === column) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(column);
            setSortDir('asc');
        }
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setIsEditModalOpen(true);
    };

    const handleSaveCategory = async (id, name) => {
        try {
            await EventsService.updateCategory(id, name);
            toast({
                title: t('category.success.updated'),
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            await fetchCategories();
        } catch (error) {
            toast({
                title: t('category.error.update'),
                status: 'error',
                duration: 3000,
                isClosable: true
            });
            throw error;
        }
    };

    const handleDeleteCategory = async (id) => {
        try {
            await EventsService.deleteCategory(id);
            toast({
                title: t('category.success.deleted'),
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            await fetchCategories();
        } catch (error) {
            toast({
                title: t('category.error.delete'),
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
    };

    return (
        <Box w="100%" maxW="7xl" px={4} py={8}>
            <Flex justify="space-between" flexDirection={{ base: 'column', md: 'row' }} className="categories-header"
                mb="48px" gap={{ base: '16px', sm: '0px' }}>
                <Flex flexDirection={"column"} className="header-left">
                    <Text className="header-title" fontFamily="body" fontWeight={'700'} fontSize={'32px'} color='#1E1517' mb="16px">
                        {t('category.page_title')}
                    </Text>
                    <Flex gap="16px" mb="16px">
                        <Input
                            placeholder={t('category.placeholder.new_name')}
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            bg="white"
                            borderColor="gray4"
                            borderRadius={'12px'}
                        />
                        <Button
                            onClick={handleCreateCategory}
                            bg="brand.500"
                            color="white"
                            borderRadius={'12px'}
                            _hover={{ bg: 'brand.600' }}
                            minW="180px"
                        >
                            {t('category.action.add')}
                        </Button>
                    </Flex>
                </Flex>
                <Flex flexDirection={"column"} className="header-right" gap="16px">
                    <Flex gap="16px">
                        <InputGroup>
                            <InputLeftElement pointerEvents="none">
                                <FiSearch color="#6E6064" />
                            </InputLeftElement>
                            <Input
                                placeholder={t('category.placeholder.search')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                bg="white"
                                borderColor="gray4"
                                borderRadius={'12px'}
                            />
                        </InputGroup>
                        <Button onClick={handleSearch} bg="brand.500" color="white" borderRadius={'12px'}
                            _hover={{ bg: 'brand.600' }} gap="10px" w="220px">
                            {t('action.search')}
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 9.75L13 12.75" stroke="white" strokeWidth="2" strokeLinecap="round"
                                    strokeLinejoin="round" />
                                <path
                                    d="M6.25 10.75C8.87335 10.75 11 8.62335 11 6C11 3.37665 8.87335 1.25 6.25 1.25C3.62665 1.25 1.5 3.37665 1.5 6C1.5 8.62335 3.62665 10.75 6.25 10.75Z"
                                    stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Button>
                    </Flex>
                </Flex>
            </Flex>

            <CategoriesTable
                categories={filteredCategories}
                sort={sort}
                sortDir={sortDir}
                onSort={handleSort}
                loading={loading}
                onEdit={handleEditCategory}
                onDelete={handleDeleteCategory}
            />

            <EditCategoryModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                category={editingCategory}
                onSave={handleSaveCategory}
            />
        </Box>
    );
};

export default CategoriesPage;